import type { BookableOffering, OfferingCatalogTree, SaveOfferingCatalogInput } from '@repo/types'
import type { ServiceRecord } from '@/lib/operator-config/types'

export type OnboardingServiceDraft = {
  id?: string
  name: string
  slug: string
  description: string
  durationMinutes: number
  price: number | null
  bufferMode: 'use_catalog_default' | 'custom'
  bufferMinutesBefore: number
  bufferMinutesAfter: number
  minimumNoticeMinutes: number
  allowQuantity: boolean
  quantityLabel: string
  maxQuantity: number
  durationPerAdditionalUnit: number
  pricePerAdditionalUnit: number
  isActive: boolean
}

export function slugifyServiceValue(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function parseCurrencyToCents(value: string): number | null {
  const normalized = value.trim()
  if (!normalized) return null
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) return null
  return Math.max(0, Math.round(numeric * 100))
}

export function formatCentsForInput(value: number | null | undefined): string {
  if (value == null) return ''
  return (value / 100).toFixed(2)
}

export const STARTER_STANDARD_VISIT_PRICE_CENTS = 12500
export const STARTER_FOLLOW_UP_VISIT_PRICE_CENTS = 8500

export function createOnboardingServiceDraft(
  patch: Partial<OnboardingServiceDraft> = {}
): OnboardingServiceDraft {
  const name = patch.name ?? ''

  return {
    name,
    slug: patch.slug ?? slugifyServiceValue(name),
    description: patch.description ?? '',
    durationMinutes: patch.durationMinutes ?? 60,
    price: patch.price ?? STARTER_STANDARD_VISIT_PRICE_CENTS,
    bufferMode: patch.bufferMode ?? 'use_catalog_default',
    bufferMinutesBefore: patch.bufferMinutesBefore ?? 0,
    bufferMinutesAfter: patch.bufferMinutesAfter ?? 0,
    minimumNoticeMinutes: patch.minimumNoticeMinutes ?? 60,
    allowQuantity: patch.allowQuantity ?? false,
    quantityLabel: patch.quantityLabel ?? 'Quantity',
    maxQuantity: patch.maxQuantity ?? 1,
    durationPerAdditionalUnit: patch.durationPerAdditionalUnit ?? 0,
    pricePerAdditionalUnit: patch.pricePerAdditionalUnit ?? 0,
    isActive: patch.isActive ?? true,
    id: patch.id,
  }
}

export function createStarterServiceDrafts(): OnboardingServiceDraft[] {
  return [
    createOnboardingServiceDraft({
      name: 'Standard Visit',
      slug: 'standard-visit',
      durationMinutes: 60,
      price: STARTER_STANDARD_VISIT_PRICE_CENTS,
    }),
    createOnboardingServiceDraft({
      name: 'Follow-up Visit',
      slug: 'follow-up-visit',
      durationMinutes: 30,
      price: STARTER_FOLLOW_UP_VISIT_PRICE_CENTS,
    }),
  ]
}

export function createServiceDraftsFromCatalog(
  catalog: OfferingCatalogTree
): OnboardingServiceDraft[] {
  return catalog.displayGroups
    .slice()
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .flatMap((group) =>
      group.offerings
        .slice()
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .map((offering: BookableOffering) =>
          createOnboardingServiceDraft({
            id: offering.id,
            name: offering.name,
            slug: offering.slug,
            description: offering.description ?? '',
            durationMinutes: offering.durationMinutes,
            price: offering.price,
            bufferMode: offering.bufferMode,
            bufferMinutesBefore: offering.bufferMinutesBefore,
            bufferMinutesAfter: offering.bufferMinutesAfter,
            minimumNoticeMinutes: offering.minimumNoticeMinutes,
            allowQuantity: offering.allowQuantity,
            quantityLabel: offering.quantityLabel ?? 'Quantity',
            maxQuantity: offering.maxQuantity ?? 1,
            durationPerAdditionalUnit: offering.durationPerAdditionalUnit ?? 0,
            pricePerAdditionalUnit: offering.pricePerAdditionalUnit ?? 0,
            isActive: offering.isActive,
          })
        )
    )
}

export function getServiceDraftReadiness(drafts: OnboardingServiceDraft[]) {
  const activeDrafts = drafts.filter((draft) => draft.isActive)
  const validActiveDrafts = activeDrafts.filter(
    (draft) => draft.name.trim() && draft.durationMinutes >= 5 && draft.price != null
  )

  let firstBlockingReason: string | null = null
  if (activeDrafts.length === 0) {
    firstBlockingReason = 'Keep at least one service active.'
  } else if (validActiveDrafts.length === 0) {
    firstBlockingReason = 'Add a name, duration, and price to at least one active service.'
  }

  return {
    canSave: firstBlockingReason == null,
    activeServiceCount: activeDrafts.length,
    validActiveServiceCount: validActiveDrafts.length,
    firstBlockingReason,
  }
}

export function buildOnboardingCatalogInput({
  catalogId,
  catalogName,
  drafts,
}: {
  catalogId?: string
  catalogName: string
  drafts: OnboardingServiceDraft[]
}): SaveOfferingCatalogInput {
  const normalizedCatalogName = catalogName.trim() || 'Starter services'

  return {
    catalog: {
      id: catalogId,
      name: normalizedCatalogName,
      slug: slugifyServiceValue(normalizedCatalogName) || 'starter-services',
      isActive: true,
      bufferMinutesBeforeDefault: 0,
      bufferMinutesAfterDefault: 0,
      minimumNoticeMinutesDefault: 60,
      displayGroups: [
        {
          name: 'Services',
          isActive: true,
          offerings: drafts
            .filter((draft) => draft.name.trim())
            .map((draft) => ({
              id: draft.id,
              name: draft.name.trim(),
              slug: slugifyServiceValue(draft.slug || draft.name) || 'service',
              description: draft.description.trim() || undefined,
              durationMinutes: Math.max(5, draft.durationMinutes),
              price: draft.price,
              allowQuantity: draft.allowQuantity,
              quantityLabel: draft.allowQuantity ? draft.quantityLabel : undefined,
              maxQuantity: draft.allowQuantity ? draft.maxQuantity : 1,
              durationPerAdditionalUnit: draft.allowQuantity ? draft.durationPerAdditionalUnit : 0,
              pricePerAdditionalUnit: draft.allowQuantity ? draft.pricePerAdditionalUnit : 0,
              bufferMode: draft.bufferMode,
              bufferMinutesBefore:
                draft.bufferMode === 'custom' ? draft.bufferMinutesBefore : undefined,
              bufferMinutesAfter:
                draft.bufferMode === 'custom' ? draft.bufferMinutesAfter : undefined,
              minimumNoticeMinutes: draft.minimumNoticeMinutes,
              isActive: draft.isActive,
            })),
        },
      ],
    },
  }
}

export function createPreviewServiceRecords(drafts: OnboardingServiceDraft[]): ServiceRecord[] {
  return drafts
    .filter((draft) => draft.name.trim())
    .map((draft, index) => ({
      id: draft.id ?? `draft-service-${index}`,
      name: draft.name.trim(),
      slug: slugifyServiceValue(draft.slug || draft.name) || `service-${index + 1}`,
      description: draft.description.trim() || null,
      durationMinutes: Math.max(5, draft.durationMinutes),
      price: draft.price,
      currency: 'usd',
      bufferMinutesBefore: draft.bufferMode === 'custom' ? draft.bufferMinutesBefore : 0,
      bufferMinutesAfter: draft.bufferMode === 'custom' ? draft.bufferMinutesAfter : 0,
      minimumNoticeMinutes: draft.minimumNoticeMinutes,
      allowQuantity: draft.allowQuantity,
      quantityLabel: draft.allowQuantity ? draft.quantityLabel : null,
      maxQuantity: draft.allowQuantity ? draft.maxQuantity : 1,
      durationPerAdditionalUnit: draft.allowQuantity ? draft.durationPerAdditionalUnit : 0,
      pricePerAdditionalUnit: draft.allowQuantity ? draft.pricePerAdditionalUnit : 0,
      isActive: draft.isActive,
      sortOrder: index,
    }))
}
